import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import * as Tone from 'tone'
import ScoreRenderer from './components/ScoreRenderer'
import PianoKeyboard from './components/PianoKeyboard'
import VoiceTabs from './components/VoiceTabs'
import DurationSelector from './components/DurationSelector'
import AccidentalSelector from './components/AccidentalSelector'
import AnalysisPanel from './components/AnalysisPanel'
import Toolbar from './components/Toolbar'
import SettingsMenu from './components/SettingsMenu'
import FileMenu from './components/FileMenu'
import {
  VOICE_NAMES, getBeatsPerMeasure,
  durationToTone, canAddNoteToCurrentMeasure,
  groupIntoMeasures,
  rotateAccidentalInPitch,
  setAccidentalInPitch,
  canReplaceDurationInMeasure,
  findPreviousPitch,
  defaultPitchForVoice,
  totalBeats,
  restEntriesForBeats,
  blankScore,
  isBlankTemplateScore,
  pitchToMidi,
  nearestLetterPitch,
} from './utils/musicTheory'
import { generateMusicXML, parseMusicXML } from './utils/musicxml'
import { buildAnalyzeHarmonyBody } from './utils/harmonyPayload'
import { analyzeHarmonyPayload, enrichWithNct } from './utils/harmonyEngine'
import { solveFourPartHarmony } from './utils/harmonizationSolver'
import { generateVoiceWithProgression, generateOuterVoicesWithProgression } from './utils/harmonyProgression'
import { gradeHarmony } from './utils/gradeHarmony'
import { DEFAULT_SOLVE_LEVEL } from './utils/solveLevels'
import { getHarmonyPlaybackEvents, toPlayableForSampler } from './utils/playHarmony'
import { useMidiDevices } from './utils/useMidiDevices'
import { useMidiInput } from './utils/useMidiInput'
import './App.css'

const BASE_URL = '/samples/salamander/'
const SAMPLES = {
  A0: 'A0.mp3',
  C1: 'C1.mp3', 'D#1': 'Ds1.mp3', 'F#1': 'Fs1.mp3', A1: 'A1.mp3',
  C2: 'C2.mp3', 'D#2': 'Ds2.mp3', 'F#2': 'Fs2.mp3', A2: 'A2.mp3',
  C3: 'C3.mp3', 'D#3': 'Ds3.mp3', 'F#3': 'Fs3.mp3', A3: 'A3.mp3',
  C4: 'C4.mp3', 'D#4': 'Ds4.mp3', 'F#4': 'Fs4.mp3', A4: 'A4.mp3',
  C5: 'C5.mp3', 'D#5': 'Ds5.mp3', 'F#5': 'Fs5.mp3', A5: 'A5.mp3',
  C6: 'C6.mp3', C7: 'C7.mp3', C8: 'C8.mp3',
}

const INITIAL_TIME = '4/4'
const STAFF_VOICES = {
  upper: ['soprano', 'alto'],
  lower: ['tenor', 'bass'],
}

/**
 * MusicXML import로 들어온 소프라노 <direction> 로마숫자(hints)를 방금 새로 계산된
 * 분석 결과 위에 덮어쓴다 — kordAI가 내보낸(또는 직접 작성한) 파일의 화음 표기를
 * 재분석으로 잃지 않고 그대로 복원하기 위함(공통화음 전조 두 줄 병기·'^' 붙임줄 포함).
 */
function applyChordSymbolHints(analysisResult, hints) {
  if (!hints?.length || !analysisResult?.measures) return
  for (const hint of hints) {
    const m = analysisResult.measures.find((mm) => Number(mm.measure) === hint.measure)
    if (!m) continue
    if (Array.isArray(m.beats) && m.beats.length > 0) {
      let target = m.beats.find((b) => Number(b.beat) === hint.beat)
      if (!target) {
        target = m.beats.reduce((best, b) => {
          const d = Math.abs(Number(b.beat) - hint.beat)
          return !best || d < Math.abs(Number(best.beat) - hint.beat) ? b : best
        }, null)
      }
      if (target) target.chordSymbol = hint.chordSymbol
    } else if (hint.beat === 0) {
      m.chordSymbol = hint.chordSymbol
    }
  }
}

export default function App() {
  /** 앱 모드: 'analyze'(4성부 화성 분석) | 'solve'(4성부 화성 풀이 — 주어진 성부 하나로 나머지 3성 채우기) */
  const [appMode, setAppMode] = useState('analyze')
  /** 풀이 모드: 문제 생성으로 채워진(주어진) 성부 — 'soprano'|'bass'|'both'|null */
  const [givenVoice, setGivenVoice] = useState(null)
  const [solving, setSolving] = useState(false)
  const [solveWarnings, setSolveWarnings] = useState([])
  /** 문제 생성 시 만들 마디 수 */
  const [measureCount, setMeasureCount] = useState(4)
  const [baseRhythm, setBaseRhythm] = useState('q')
  /** 풀이 레벨: 백병동 N장까지 누적 (기본 3=속7화음) */
  const [solveLevel, setSolveLevel] = useState(DEFAULT_SOLVE_LEVEL)
  /** 마지막 생성한 화성진행 (베이스/소프라노 문제용) */
  const [solveProgression, setSolveProgression] = useState(null)
  const [score, setScore]             = useState(() => blankScore(getBeatsPerMeasure(INITIAL_TIME)))
  const [history, setHistory]         = useState([])
  const [activeVoice, setActiveVoice] = useState('soprano')
  const [duration, setDuration]       = useState('q')
  /** 임시표 패드: 음표 선택 중이면 즉시 적용, 아니면 다음 입력에 쓸 임시표를 무장(sticky)해 둔다 */
  const [pendingAccidental, setPendingAccidental] = useState('')
  const [keySignature, setKeySignature]   = useState('C')
  const [timeSignature, setTimeSignature] = useState(INITIAL_TIME)
  /** 분당 박자 (템포). 건반·화음 재생 길이에 반영 */
  const [tempoBpm, setTempoBpm] = useState(120)
  /** 화성 분석 기반지식: 'baek'(백병동 화성학) | 'kostka'(Kostka & Payne) */
  const [theoryBase, setTheoryBase] = useState('baek')
  /** 재생 볼륨 (0~100) */
  const [volume, setVolume] = useState(80)
  /** 설정(MIDI) 패널 */
  const [settingsOpen, setSettingsOpen] = useState(false)
  /** 파일 메뉴 드롭다운 (브라우저) */
  const [fileMenuOpen, setFileMenuOpen] = useState(false)
  const isElectron = typeof window !== 'undefined' && Boolean(window.kordAI?.isElectron)
  const xmlFileRef = useRef(null)
  const [analysisResult, setAnalysisResult] = useState(null)
  /** MusicXML import 시 소프라노 <direction> 로마숫자를 그대로 복원하기 위한 대기열 — 다음 분석 결과에 1회 적용 후 비운다. */
  const chordSymbolHintsRef = useRef([])
  const [analysisLoading, setAnalysisLoading] = useState(false)
  const [analysisError, setAnalysisError] = useState(null)
  const [showAnalysis, setShowAnalysis] = useState(false)
  const [playingHarmony, setPlayingHarmony] = useState(false)
  /** 선택된 음표/쉼표: ScoreRenderer와 공유 */
  const [selectedNote, setSelectedNote] = useState(null)
  /** 마디 빈 곳 클릭: 위 스탭(소·알) / 아래 스탭(테·베) 구분 */
  const [selectedMeasure, setSelectedMeasure] = useState(null)
  /** 재생 커서: 마디 인덱스(0~) + 그 마디 안에서의 박(비트) 오프셋 */
  const [playbackCursor, setPlaybackCursor] = useState(null)
  /** 외부 MIDI 건반에서 누르고 있는 음 (하이라이트용 midi number) */
  const [pressedMidis, setPressedMidis] = useState(() => new Set())
  const samplerRef = useRef(null)
  const samplerLoaded = useRef(false)
  const harmonyPlaybackRunRef = useRef(0)

  const givenVoices = givenVoice === 'both'
    ? ['soprano', 'bass']
    : givenVoice
      ? [givenVoice]
      : []
  /** 풀이: 주어진(문제) 성부는 잠그고, 채울 성부만 건반 입력 */
  const solveDisabledVoices = appMode === 'solve'
    ? (givenVoice ? givenVoices : [...VOICE_NAMES])
    : []
  /** 화음 재생: 다음 마디 화음을 예약한 timeout (중단 시 clear) */
  const harmonyScheduleRef = useRef(null)
  const analysisSectionRef = useRef(null)
  const midiSendNoteOnRef = useRef(() => {})
  const midiSendNoteOffRef = useRef(() => {})

  // Load Tone.js Salamander piano sampler
  useEffect(() => {
    const s = new Tone.Sampler({
      urls: SAMPLES,
      baseUrl: BASE_URL,
      onload: () => { samplerLoaded.current = true },
    }).toDestination()
    samplerRef.current = s
    return () => s.dispose()
  }, [])

  useEffect(() => {
    const unsub = window.kordAI?.onOpenMidiSettings?.(() => setSettingsOpen(true))
    return typeof unsub === 'function' ? unsub : undefined
  }, [])

  const openMusicXMLPicker = useCallback(() => {
    xmlFileRef.current?.click()
  }, [])

  const handleExportScore = useCallback(() => {
    const xml = generateMusicXML(score, keySignature, timeSignature, tempoBpm, analysisResult)
    if (!xml) return
    const blob = new Blob([xml], { type: 'application/xml;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'kordAI_score.xml'
    a.click()
    URL.revokeObjectURL(url)
  }, [score, keySignature, timeSignature, tempoBpm, analysisResult])

  const handleExportReport = useCallback(() => {
    const payload = {
      keySignature,
      timeSignature,
      tempoBpm,
      theoryBase,
      score,
      analysisResult,
    }
    const json = JSON.stringify(payload, null, 2)
    const blob = new Blob([json], { type: 'application/json;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'kordAI_harmony_report.json'
    a.click()
    URL.revokeObjectURL(url)
  }, [score, keySignature, timeSignature, tempoBpm, theoryBase, analysisResult])

  useEffect(() => {
    const unsubOpen = window.kordAI?.onFileOpen?.(openMusicXMLPicker)
    const unsubSaveScore = window.kordAI?.onFileSaveScore?.(handleExportScore)
    const unsubSaveReport = window.kordAI?.onFileSaveReport?.(handleExportReport)
    return () => {
      if (typeof unsubOpen === 'function') unsubOpen()
      if (typeof unsubSaveScore === 'function') unsubSaveScore()
      if (typeof unsubSaveReport === 'function') unsubSaveReport()
    }
  }, [openMusicXMLPicker, handleExportScore, handleExportReport])

  // Undo: Ctrl+Z — 열기/저장 단축키는 Electron 네이티브 액셀러레이터 또는 아래 브라우저 핸들러
  useEffect(() => {
    const handler = e => {
      if (!(e.ctrlKey || e.metaKey)) return
      const key = e.key.toLowerCase()
      if (key === 'z') {
        e.preventDefault()
        if (history.length > 0) {
          setScore(history[history.length - 1])
          setHistory(h => h.slice(0, -1))
        }
        return
      }
      if (isElectron) return
      if (key === 'o') {
        e.preventDefault()
        openMusicXMLPicker()
        return
      }
      if (key === 's') {
        e.preventDefault()
        if (!VOICE_NAMES.some(v => score[v].length > 0)) return
        if (e.shiftKey) handleExportReport()
        else handleExportScore()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [history, openMusicXMLPicker, handleExportScore, handleExportReport, score, isElectron])

  const beatsPerMeasure = getBeatsPerMeasure(timeSignature)

  useEffect(() => {
    Tone.getTransport().bpm.value = tempoBpm
  }, [tempoBpm])

  useEffect(() => {
    const s = samplerRef.current
    if (!s) return
    s.volume.value = volume <= 0 ? -Infinity : Tone.gainToDb(volume / 100)
  }, [volume])

  const canExport  = VOICE_NAMES.some(v => score[v].length > 0)
  const canPlayHarmony = useMemo(
    () => getHarmonyPlaybackEvents(score, timeSignature).length > 0,
    [score, timeSignature],
  )

  const handleSelectNote = useCallback((n) => {
    if (n) {
      setSelectedMeasure(null)
      if (n.duration) setDuration(n.duration)
      if (n.voice) setActiveVoice(n.voice)
      // 아직 실제 데이터가 없는 자리표시자 쉼표(빈 마디 뒤쪽)를 클릭한 경우 —
      // 거기까지 실제 쉼표를 채워 넣어야 커서로 선택하고 이어서 입력할 수 있다.
      if (n.isFiller && n.padEntries?.length) {
        setHistory((h) => [...h, score])
        const voice = n.voice
        const pad = n.padEntries
        const baseLen = score[voice]?.length ?? 0
        setScore((s) => ({ ...s, [voice]: [...s[voice], ...pad] }))
        // materialize 직후 flatIndex는 기존 길이 + (pad 마지막 = 클릭한 자리)
        setSelectedNote({
          voice,
          flatIndex: baseLen + pad.length - 1,
          isRest: true,
          duration: pad[pad.length - 1]?.duration ?? n.duration,
        })
        return
      }
    }
    setSelectedNote(n)
  }, [score])

  const handleSelectMeasure = useCallback((sel) => {
    setSelectedMeasure(sel)
    if (!sel) return
    setSelectedNote(null)
    // 이미 선택된 성부가 클릭한 스탭(위/아래)에 속하면 그대로 유지.
    // (예: 알토 탭을 켜놓고 위 스탭 빈 마디를 클릭해도 소프라노로 바뀌지 않게)
    setActiveVoice((cur) => {
      const staffVoices = sel.staff === 'upper' ? ['soprano', 'alto'] : ['tenor', 'bass']
      return staffVoices.includes(cur) ? cur : staffVoices[0]
    })
  }, [])

  const handleDurationChange = useCallback((newDur) => {
    if (!selectedNote || selectedNote.flatIndex == null) {
      setDuration(newDur)
      return
    }
    const { voice, flatIndex } = selectedNote
    const voiceNotes = score[voice]
    if (!voiceNotes || flatIndex < 0 || flatIndex >= voiceNotes.length) {
      setDuration(newDur)
      return
    }
    const cur = voiceNotes[flatIndex]
    if (newDur === cur.duration) {
      setDuration(newDur)
      return
    }
    if (!canReplaceDurationInMeasure(voiceNotes, flatIndex, newDur, beatsPerMeasure)) {
      console.warn('Measure full: cannot use this duration in this bar.')
      return
    }

    setDuration(newDur)
    setHistory((h) => [...h, score])

    const toRest = newDur.includes('r')
    let nextPitch = cur.pitch
    if (!toRest && (cur.isRest || !nextPitch)) {
      nextPitch = findPreviousPitch(voiceNotes, flatIndex) ?? defaultPitchForVoice(voice)
    }
    const nextEntry = toRest
      ? { duration: newDur, isRest: true }
      : { duration: newDur, isRest: false, pitch: nextPitch }

    setScore((s) => {
      const arr = [...s[voice]]
      arr[flatIndex] = nextEntry
      return { ...s, [voice]: arr }
    })

    let noteIndex
    if (!toRest) {
      let c = 0
      for (let i = 0; i < flatIndex; i++) {
        if (!voiceNotes[i].isRest) c++
      }
      noteIndex = c
    }

    handleSelectNote({
      ...selectedNote,
      duration: newDur,
      isRest: toRest,
      pitch: toRest ? undefined : nextPitch,
      noteIndex,
    })
  }, [selectedNote, score, beatsPerMeasure, handleSelectNote])

  const addNote = useCallback((pitch) => {
    // 클릭해서 선택해 둔 마디가 activeVoice의 스탭에 속하고, 아직 그 마디까지
    // 실제 데이터가 채워지지 않은(=미래의 빈 마디) 경우, 항상 배열 끝에만 이어붙이면
    // 클릭 위치를 무시하고 엉뚱하게 맨 뒤에 붙어버린다. 그 마디 시작 지점까지
    // 쉼표로 채워서 입력한 음이 실제로 그 마디에 들어가도록 한다.
    let padEntries = []
    if (selectedMeasure) {
      const staffVoices = selectedMeasure.staff === 'upper' ? ['soprano', 'alto'] : ['tenor', 'bass']
      if (staffVoices.includes(activeVoice)) {
        const targetOffset = selectedMeasure.measureIndex * beatsPerMeasure
        const existing = totalBeats(score[activeVoice])
        if (existing < targetOffset - 0.001) {
          padEntries = restEntriesForBeats(targetOffset - existing)
        }
      }
    }
    setSelectedMeasure(null)

    const notesWithPad = [...score[activeVoice], ...padEntries]
    // Check if note can be added to current measure
    if (!canAddNoteToCurrentMeasure(notesWithPad, duration, beatsPerMeasure)) {
      // Show warning or prevent addition
      console.warn('Cannot add note: measure would exceed beats per measure')
      return
    }

    setHistory(h => [...h, score])

    // Check if duration is a rest
    const newEntry = duration.includes('r')
      ? { duration, isRest: true }
      : { pitch, duration }
    setScore(s => ({
      ...s,
      [activeVoice]: [...s[activeVoice], ...padEntries, newEntry]
    }))
  }, [score, activeVoice, duration, beatsPerMeasure, selectedMeasure])

  const handleKeyClick = useCallback(async (rawPitch, opts = {}) => {
    // 임시표 패드가 무장돼 있으면(다음 입력 대기 중) 건반이 준 음이름에 그 임시표를 덮어씌운다
    const pitch = pendingAccidental && !opts.fromMidi
      ? setAccidentalInPitch(rawPitch, pendingAccidental)
      : rawPitch
    const voiceLocked = appMode === 'solve' && givenVoice && givenVoices.includes(activeVoice)
    if (voiceLocked) return

    // 오디오 재생 (+ MIDI Out — 입력 장치에서 온 음은 루프 방지로 제외)
    try {
      await Tone.start()
      Tone.getTransport().bpm.value = tempoBpm
      if (samplerLoaded.current && samplerRef.current) {
        samplerRef.current.triggerAttackRelease(toPlayableForSampler(pitch), durationToTone(duration))
      }
      if (!opts.fromMidi) {
        const midi = pitchToMidi(pitch)
        if (midi != null) {
          midiSendNoteOnRef.current(midi)
          let holdMs = 400
          try {
            holdMs = Math.max(80, Tone.Time(durationToTone(duration)).toSeconds() * 1000)
          } catch { /* ignore */ }
          window.setTimeout(() => midiSendNoteOffRef.current(midi), holdMs)
        }
      }
    } catch (e) { /* ignore audio errors */ }

    if (selectedNote) {
      // 대치 모드: 선택된 위치의 음을 교체하고 커서 전진
      const voice = selectedNote.voice
      if (appMode === 'solve' && givenVoice && givenVoices.includes(voice)) return
      const voiceNotes = score[voice]
      const flatIdx = selectedNote.flatIndex

      // 현재 활성 리듬 사용 (쉼표 기호 제거)
      const replaceDur = duration.replace('r', '')

      // 새 리듬이 마디에 맞는지 확인
      if (!canReplaceDurationInMeasure(voiceNotes, flatIdx, replaceDur, beatsPerMeasure)) {
        console.warn('Cannot replace: new duration does not fit in this measure.')
        return
      }

      setHistory(h => [...h, score])
      setScore(s => {
        const arr = [...s[voice]]
        arr[flatIdx] = { pitch, duration: replaceDur }
        return { ...s, [voice]: arr }
      })

      // 다음 위치로 커서 전진
      const nextFlat = flatIdx + 1
      if (nextFlat < voiceNotes.length) {
        const nextEntry = voiceNotes[nextFlat]
        // noteIndex 계산: flatIdx는 이제 반드시 비-쉼표이므로 그 기준으로 카운트
        let nonRestBefore = 0
        for (let i = 0; i < nextFlat; i++) {
          if (i === flatIdx) {
            nonRestBefore++ // 방금 비-쉼표로 대치한 자리
          } else if (!voiceNotes[i].isRest) {
            nonRestBefore++
          }
        }
        const nextNoteIndex = nextEntry?.isRest ? undefined : nonRestBefore
        handleSelectNote({
          voice,
          flatIndex: nextFlat,
          isRest: nextEntry?.isRest ?? false,
          noteIndex: nextNoteIndex,
          pitch: nextEntry?.pitch,
          duration: nextEntry?.duration,
        })
      } else {
        // 끝에 도달 → 선택 해제, 이어서 입력 모드
        handleSelectNote(null)
        setActiveVoice(voice)
      }
      return
    }

    // 선택 없이 건반: 맨 끝 마디가 "쉼표만으로 꽉 찬" 자리표시자면 새 마디를 열지 말고
    // 그 쉼표를 대치한다 (초기 온쉼표 템플릿 / 미기입 성부).
    if (!selectedMeasure) {
      const voice = activeVoice
      const voiceNotes = score[voice]
      const measures = groupIntoMeasures(voiceNotes, beatsPerMeasure)
      const last = measures[measures.length - 1]
      if (last?.complete && last.notes.length > 0 && last.notes.every((n) => n.isRest)) {
        let flatIdx = 0
        for (let i = 0; i < measures.length - 1; i++) flatIdx += measures[i].notes.length
        const replaceDur = duration.replace('r', '')
        if (canReplaceDurationInMeasure(voiceNotes, flatIdx, replaceDur, beatsPerMeasure)) {
          setHistory((h) => [...h, score])
          setScore((s) => {
            const arr = [...s[voice]]
            arr[flatIdx] = { pitch, duration: replaceDur }
            return { ...s, [voice]: arr }
          })
          const nextFlat = flatIdx + 1
          if (nextFlat < voiceNotes.length) {
            const nextEntry = voiceNotes[nextFlat]
            handleSelectNote({
              voice,
              flatIndex: nextFlat,
              isRest: nextEntry?.isRest ?? false,
              duration: nextEntry?.duration,
              pitch: nextEntry?.pitch,
            })
          } else {
            handleSelectNote(null)
            setActiveVoice(voice)
          }
          return
        }
      }
    }

    addNote(pitch)
  }, [
    selectedNote, score, addNote, duration, tempoBpm, handleSelectNote,
    appMode, givenVoice, givenVoices, activeVoice, beatsPerMeasure, pendingAccidental,
    selectedMeasure,
  ])

  /**
   * 시벨리우스식 컴퓨터 자판 음표 입력. A~G: 직전 음에서 가장 가까운 옥타브에 그 음이름을
   * 놓고 handleKeyClick과 동일한 경로(선택돼 있으면 대치+커서 전진, 아니면 append)로 넣는다.
   */
  const enterLetterNote = useCallback((letter) => {
    const voice = selectedNote ? selectedNote.voice : activeVoice
    if (appMode === 'solve' && givenVoice && givenVoices.includes(voice)) return
    const voiceNotes = score[voice]
    const flatIdx = selectedNote ? selectedNote.flatIndex : voiceNotes.length
    const refPitch = findPreviousPitch(voiceNotes, flatIdx) ?? defaultPitchForVoice(voice)
    const pitch = nearestLetterPitch(letter, refPitch, keySignature)
    handleKeyClick(pitch)
  }, [selectedNote, activeVoice, score, appMode, givenVoice, givenVoices, keySignature, handleKeyClick])

  /**
   * 현재 선택된 길이(duration)의 쉼표를 커서 위치에 넣는다 — 선택된 음이 있으면 그 자리를
   * 쉼표로 대치하고 다음 자리로 커서 전진, 없으면 activeVoice 끝에 이어붙인다. handleKeyClick의
   * 대치/append 분기 구조를 그대로 따르되(피치 대신) 쉼표 엔트리를 만든다는 점만 다르다.
   */
  const insertRestStep = useCallback(() => {
    const restDur = duration.replace('r', '') + 'r'

    if (selectedNote) {
      const voice = selectedNote.voice
      if (appMode === 'solve' && givenVoice && givenVoices.includes(voice)) return
      const voiceNotes = score[voice]
      const flatIdx = selectedNote.flatIndex

      if (!canReplaceDurationInMeasure(voiceNotes, flatIdx, restDur, beatsPerMeasure)) {
        console.warn('Cannot replace: new duration does not fit in this measure.')
        return
      }

      setHistory(h => [...h, score])
      setScore(s => {
        const arr = [...s[voice]]
        arr[flatIdx] = { duration: restDur, isRest: true }
        return { ...s, [voice]: arr }
      })

      const nextFlat = flatIdx + 1
      if (nextFlat < voiceNotes.length) {
        const nextEntry = voiceNotes[nextFlat]
        let nonRestBefore = 0
        for (let i = 0; i < nextFlat; i++) {
          if (i === flatIdx) continue // 방금 쉼표로 바뀐 자리 — 비-쉼표 카운트에서 제외
          if (!voiceNotes[i].isRest) nonRestBefore++
        }
        const nextNoteIndex = nextEntry?.isRest ? undefined : nonRestBefore
        handleSelectNote({
          voice,
          flatIndex: nextFlat,
          isRest: nextEntry?.isRest ?? false,
          noteIndex: nextNoteIndex,
          pitch: nextEntry?.pitch,
          duration: nextEntry?.duration,
        })
      } else {
        handleSelectNote(null)
        setActiveVoice(voice)
      }
      return
    }

    const voice = activeVoice
    if (appMode === 'solve' && givenVoice && givenVoices.includes(voice)) return
    let padEntries = []
    if (selectedMeasure) {
      const staffVoices = selectedMeasure.staff === 'upper' ? ['soprano', 'alto'] : ['tenor', 'bass']
      if (staffVoices.includes(voice)) {
        const targetOffset = selectedMeasure.measureIndex * beatsPerMeasure
        const existing = totalBeats(score[voice])
        if (existing < targetOffset - 0.001) {
          padEntries = restEntriesForBeats(targetOffset - existing)
        }
      }
    }
    setSelectedMeasure(null)

    const notesWithPad = [...score[voice], ...padEntries]
    if (!canAddNoteToCurrentMeasure(notesWithPad, restDur, beatsPerMeasure)) {
      console.warn('Cannot add note: measure would exceed beats per measure')
      return
    }

    setHistory(h => [...h, score])
    setScore(s => ({
      ...s,
      [voice]: [...s[voice], ...padEntries, { duration: restDur, isRest: true }],
    }))
  }, [
    selectedNote, score, activeVoice, duration, beatsPerMeasure, selectedMeasure,
    appMode, givenVoice, givenVoices, handleSelectNote,
  ])

  /** 화살표 좌/우: 클릭 없이 선택 커서를 같은 성부의 이전/다음 음(쉼표 포함)으로 옮긴다. */
  const moveSelectionCursor = useCallback((dir) => {
    if (!selectedNote) return
    const { voice, flatIndex } = selectedNote
    const voiceNotes = score[voice]
    const nextFlat = flatIndex + dir
    if (nextFlat < 0 || nextFlat >= voiceNotes.length) return
    const entry = voiceNotes[nextFlat]
    let nonRestBefore = 0
    for (let i = 0; i < nextFlat; i++) {
      if (!voiceNotes[i].isRest) nonRestBefore++
    }
    handleSelectNote({
      voice,
      flatIndex: nextFlat,
      isRest: entry?.isRest ?? false,
      noteIndex: entry?.isRest ? undefined : nonRestBefore,
      pitch: entry?.pitch,
      duration: entry?.duration,
    })
  }, [selectedNote, score, handleSelectNote])

  // 시벨리우스식 자판 입력: 숫자 1~4=음가, R=쉼표, A~G=음이름, ←→=커서 이동, Esc=선택 해제
  useEffect(() => {
    const DURATION_KEYS = { '1': 'w', '2': 'h', '3': 'q', '4': '8' }
    const onNoteInputKey = (e) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      if (e.ctrlKey || e.metaKey || e.altKey) return

      if (DURATION_KEYS[e.key]) {
        e.preventDefault()
        setDuration(DURATION_KEYS[e.key])
        return
      }
      if (e.key.toLowerCase() === 'r' && !e.repeat) {
        e.preventDefault()
        insertRestStep()
        return
      }
      if (e.key === 'Escape') {
        if (selectedNote || selectedMeasure) {
          e.preventDefault()
          handleSelectNote(null)
          setSelectedMeasure(null)
        }
        return
      }
      if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && selectedNote) {
        e.preventDefault()
        moveSelectionCursor(e.key === 'ArrowLeft' ? -1 : 1)
        return
      }
      if (e.key.length === 1 && /^[A-Ga-g]$/.test(e.key) && !e.repeat) {
        e.preventDefault()
        enterLetterNote(e.key.toUpperCase())
      }
    }
    window.addEventListener('keydown', onNoteInputKey)
    return () => window.removeEventListener('keydown', onNoteInputKey)
  }, [enterLetterNote, insertRestStep, moveSelectionCursor, selectedNote, selectedMeasure, handleSelectNote])

  const {
    status: midiDeviceStatus,
    inputs: midiInputs,
    outputs: midiOutputs,
    inputId: midiInputId,
    outputId: midiOutputId,
    setInputId: setMidiInputId,
    setOutputId: setMidiOutputId,
    refresh: refreshMidiDevices,
    sendNoteOn: midiSendNoteOn,
    sendNoteOff: midiSendNoteOff,
  } = useMidiDevices()

  useEffect(() => { midiSendNoteOnRef.current = midiSendNoteOn }, [midiSendNoteOn])
  useEffect(() => { midiSendNoteOffRef.current = midiSendNoteOff }, [midiSendNoteOff])

  useEffect(() => {
    setPressedMidis(new Set())
  }, [midiInputId])

  const { status: midiStatus } = useMidiInput({
    enabled: true,
    inputId: midiInputId,
    keySignature,
    onNoteOn: (pitch, midi) => {
      setPressedMidis((prev) => {
        if (prev.has(midi)) return prev
        const next = new Set(prev)
        next.add(midi)
        return next
      })
      handleKeyClick(pitch, { fromMidi: true })
    },
    onNoteOff: (_pitch, midi) => {
      setPressedMidis((prev) => {
        if (!prev.has(midi)) return prev
        const next = new Set(prev)
        next.delete(midi)
        return next
      })
    },
  })

  const handlePlayHarmony = useCallback(async () => {
    const events = getHarmonyPlaybackEvents(score, timeSignature)
    if (events.length === 0) return
    const myRun = ++harmonyPlaybackRunRef.current
    if (harmonyScheduleRef.current != null) {
      clearTimeout(harmonyScheduleRef.current)
      harmonyScheduleRef.current = null
    }
    try {
      await Tone.start()
      await Tone.loaded()
      const sampler = samplerRef.current
      if (!sampler || !sampler.loaded) {
        setPlayingHarmony(false)
        setPlaybackCursor(null)
        return
      }
      setPlayingHarmony(true)
      setPlaybackCursor(null)
      Tone.getTransport().bpm.value = tempoBpm
      const beatMs = Tone.Time('4n').toSeconds() * 1000
      const bpp = getBeatsPerMeasure(timeSignature)
      const leadMs = 100

      // 각 이벤트의 실제 유지 시간 = 다음 이벤트까지의 박 간격
      const timedEvents = events.map((ev, idx) => {
        const next = events[idx + 1]
        let holdBeats
        if (!next) {
          holdBeats = Math.max(1, bpp - ev.beatInMeasure)
        } else if (next.measureIndex === ev.measureIndex) {
          holdBeats = next.beatInMeasure - ev.beatInMeasure
        } else {
          holdBeats = (bpp - ev.beatInMeasure) + next.beatInMeasure
        }
        return { ...ev, holdMs: Math.max(beatMs * 0.5, holdBeats * beatMs) }
      })

      // 성부별 독립 추적: 새 onset이 있는 성부만 release→attack, 나머지는 계속 울림
      const activeByVoice = { soprano: null, alto: null, tenor: null, bass: null }
      const activeMidiByVoice = { soprano: null, alto: null, tenor: null, bass: null }

      const playChordAt = (index) => {
        if (harmonyPlaybackRunRef.current !== myRun) return
        if (index >= timedEvents.length) {
          // 모든 성부 release
          const toRelease = Object.values(activeByVoice).filter(Boolean).map(toPlayableForSampler)
          if (toRelease.length > 0) sampler.triggerRelease(toRelease, Tone.now())
          for (const voice of ['soprano', 'alto', 'tenor', 'bass']) {
            if (activeMidiByVoice[voice] != null) {
              midiSendNoteOffRef.current(activeMidiByVoice[voice])
              activeMidiByVoice[voice] = null
            }
          }
          harmonyScheduleRef.current = null
          setPlayingHarmony(false)
          setPlaybackCursor(null)
          return
        }
        const ev = timedEvents[index]
        const toRelease = []
        const toAttack = []
        for (const voice of ['soprano', 'alto', 'tenor', 'bass']) {
          const newPitch = ev.voices?.[voice]
          if (newPitch) {
            // 이 성부에 새 onset: 이전 음 release 후 새 음 attack
            if (activeByVoice[voice]) toRelease.push(toPlayableForSampler(activeByVoice[voice]))
            if (activeMidiByVoice[voice] != null) {
              midiSendNoteOffRef.current(activeMidiByVoice[voice])
              activeMidiByVoice[voice] = null
            }
            toAttack.push(toPlayableForSampler(newPitch))
            activeByVoice[voice] = newPitch
            const m = pitchToMidi(newPitch)
            if (m != null) {
              midiSendNoteOnRef.current(m)
              activeMidiByVoice[voice] = m
            }
          }
          // onset 없는 성부는 그대로 계속 울림
        }
        if (toRelease.length > 0) sampler.triggerRelease(toRelease, Tone.now())
        if (toAttack.length > 0) sampler.triggerAttack(toAttack, Tone.now())
        setPlaybackCursor({
          measureIndex: ev.measureIndex,
          beatInMeasure: ev.beatInMeasure,
        })
        harmonyScheduleRef.current = window.setTimeout(() => playChordAt(index + 1), ev.holdMs)
      }

      harmonyScheduleRef.current = window.setTimeout(() => playChordAt(0), leadMs)
    } catch {
      setPlayingHarmony(false)
      setPlaybackCursor(null)
    }
  }, [score, timeSignature, tempoBpm])

  const handleStopHarmony = useCallback(() => {
    harmonyPlaybackRunRef.current += 1
    if (harmonyScheduleRef.current != null) {
      clearTimeout(harmonyScheduleRef.current)
      harmonyScheduleRef.current = null
    }
    try {
      const s = samplerRef.current
      if (s && typeof s.releaseAll === 'function') s.releaseAll()
    } catch { /* ignore */ }
    setPlayingHarmony(false)
    setPlaybackCursor(null)
  }, [])

  /** 악보·분석·선택·풀이 문제를 모두 비우고 빈 마디(성부별 쉼표)로 시작 */
  const handleNewBlank = useCallback(() => {
    const hasRealNotes = VOICE_NAMES.some((v) => score[v]?.some((n) => !n.isRest))
    const hasExtraBars = !isBlankTemplateScore(score, beatsPerMeasure)
    const hasScore = hasRealNotes || hasExtraBars
    const hasAnalysis = Boolean(analysisResult || analysisError || showAnalysis || analysisLoading)
    const hasSolve = Boolean(givenVoice || solveProgression || solveWarnings.length)
    if (hasScore || hasAnalysis || hasSolve) {
      const ok = window.confirm('화면에 있는 악보와 분석 결과를 모두 지울까요?')
      if (!ok) return
    }
    handleStopHarmony()
    setScore(blankScore(beatsPerMeasure))
    setHistory([])
    setAnalysisResult(null)
    setAnalysisError(null)
    setAnalysisLoading(false)
    setShowAnalysis(false)
    setSelectedNote(null)
    setSelectedMeasure(null)
    setPressedMidis(new Set())
    setGivenVoice(null)
    setSolveWarnings([])
    setSolveProgression(null)
    setSolving(false)
  }, [
    score,
    beatsPerMeasure,
    analysisResult,
    analysisError,
    showAnalysis,
    analysisLoading,
    givenVoice,
    solveProgression,
    solveWarnings.length,
    handleStopHarmony,
  ])

  useEffect(() => {
    const unsubNew = window.kordAI?.onFileNew?.(handleNewBlank)
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return
      if (e.key.toLowerCase() !== 'n') return
      // Electron은 네이티브 액셀러레이터가 담당
      if (typeof window !== 'undefined' && window.kordAI?.isElectron) return
      e.preventDefault()
      handleNewBlank()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      if (typeof unsubNew === 'function') unsubNew()
      window.removeEventListener('keydown', onKey)
    }
  }, [handleNewBlank])

  const clampTempoBpm = useCallback((n) => Math.min(320, Math.max(20, Math.round(n))), [])

  const handleTempoBpmChange = useCallback((value) => {
    const n = parseInt(String(value).replace(/\D/g, ''), 10)
    if (Number.isNaN(n)) return
    setTempoBpm(clampTempoBpm(n))
  }, [clampTempoBpm])

  const handleDragStart = useCallback(() => {
    setHistory(h => [...h, score])
  }, [score])

  const handleNoteChange = useCallback((voice, noteIndex, newPitch) => {
    setScore(s => {
      const voiceNotes = [...s[voice]]
      let nonRestCount = 0
      for (let i = 0; i < voiceNotes.length; i++) {
        if (!voiceNotes[i].isRest) {
          if (nonRestCount === noteIndex) {
            voiceNotes[i] = { ...voiceNotes[i], pitch: newPitch }
            break
          }
          nonRestCount++
        }
      }
      return { ...s, [voice]: voiceNotes }
    })
  }, [])

  /**
   * 임시표 패드 버튼: 음표가 선택 중이면 그 자리에 바로 적용, 아니면 다음 입력에
   * 쓸 임시표로 무장(sticky)해 둔다 — handleKeyClick/addNote가 pendingAccidental을 읽어 적용.
   */
  const handleAccidentalSelect = useCallback((acc) => {
    if (
      selectedNote
      && !selectedNote.isRest
      && selectedNote.noteIndex != null
      && selectedNote.pitch
    ) {
      const nextPitch = setAccidentalInPitch(selectedNote.pitch, acc)
      setHistory((h) => [...h, score])
      handleNoteChange(selectedNote.voice, selectedNote.noteIndex, nextPitch)
      handleSelectNote({ ...selectedNote, pitch: nextPitch })
      return
    }
    setPendingAccidental((cur) => (cur === acc ? '' : acc))
  }, [selectedNote, score, handleNoteChange, handleSelectNote])

  // Enter: cycle accidental (none, sharp, flat, natural)
  useEffect(() => {
    const onEnter = (e) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      if (e.key !== 'Enter') return
      if (
        !selectedNote
        || selectedNote.isRest
        || selectedNote.noteIndex == null
        || !selectedNote.pitch
      ) {
        return
      }
      e.preventDefault()
      const nextPitch = rotateAccidentalInPitch(selectedNote.pitch)
      setHistory((h) => [...h, score])
      handleNoteChange(selectedNote.voice, selectedNote.noteIndex, nextPitch)
      handleSelectNote({
        ...selectedNote,
        pitch: nextPitch,
      })
    }
    window.addEventListener('keydown', onEnter)
    return () => window.removeEventListener('keydown', onEnter)
  }, [selectedNote, score, handleNoteChange, handleSelectNote])

  const handleMeasureClear = useCallback((measureIndex, staff = null) => {
    setSelectedMeasure((sm) => (sm && sm.measureIndex === measureIndex ? null : sm))
    const targetVoices = staff && STAFF_VOICES[staff] ? STAFF_VOICES[staff] : VOICE_NAMES
    setHistory(h => [...h, score])
    setScore(s => {
      const next = { ...s }
      for (const voice of targetVoices) {
        const measures = groupIntoMeasures(s[voice], beatsPerMeasure)
        if (measureIndex >= measures.length) continue
        let startIdx = 0
        for (let i = 0; i < measureIndex; i++) startIdx += measures[i].notes.length
        const voiceNotes = [...s[voice]]
        for (let i = 0; i < measures[measureIndex].notes.length; i++) {
          const n = voiceNotes[startIdx + i]
          if (!n.isRest) {
            voiceNotes[startIdx + i] = { duration: n.duration + 'r', isRest: true }
          }
        }
        next[voice] = voiceNotes
      }
      return next
    })
  }, [score, beatsPerMeasure])

  const handleNoteDelete = useCallback((voice, noteIndex) => {
    setHistory(h => [...h, score])
    setScore(s => {
      const voiceNotes = [...s[voice]]
      let nonRestCount = 0
      for (let i = 0; i < voiceNotes.length; i++) {
        if (!voiceNotes[i].isRest) {
          if (nonRestCount === noteIndex) {
            // 음표를 같은 길이의 쉼표로 대체해 마디 구조 유지
            const dur = voiceNotes[i].duration
            voiceNotes[i] = { duration: dur + 'r', isRest: true }
            break
          }
          nonRestCount++
        }
      }
      return { ...s, [voice]: voiceNotes }
    })
  }, [score])

  const handleImportMusicXML = useCallback((e) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      try {
        const { score: next, keySignature: ks, timeSignature: ts, chordSymbolHints } = parseMusicXML(String(reader.result))
        setHistory((h) => [...h, score])
        setScore(next)
        setKeySignature(ks)
        setTimeSignature(ts)
        setAnalysisResult(null)
        setSelectedMeasure(null)
        chordSymbolHintsRef.current = chordSymbolHints ?? []
      } catch (err) {
        window.alert(err.message || String(err))
      }
    }
    reader.onerror = () => {
      window.alert('파일을 읽을 수 없습니다.')
    }
    reader.readAsText(file)
    e.target.value = ''
  }, [score])

  const handleAnalyze = async () => {
    setShowAnalysis(true)
    if (!VOICE_NAMES.some((v) => score[v].length > 0)) {
      setAnalysisError('입력된 음이 없습니다.')
      setAnalysisResult(null)
      setAnalysisLoading(false)
      queueMicrotask(() => {
        analysisSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      })
      return
    }
    setAnalysisLoading(true)
    setAnalysisError(null)
    setAnalysisResult(null)
    try {
      const body = buildAnalyzeHarmonyBody(score, keySignature, timeSignature, theoryBase)
      let out = null
      // 임시 진단 로그 — "실제 사용 중 백엔드 vs JS 폴백 중 뭐가 타는지" 확인용 (분석 몇 번 해본 뒤 삭제 예정).
      const diag = { source: null, httpStatus: null, fetchError: null }
      try {
        const res = await fetch('/analyze-harmony', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
        diag.httpStatus = res.status
        if (res.ok) {
          out = await res.json()
        }
      } catch (e) {
        diag.fetchError = e?.message || String(e)
        /* 백엔드 미기동·CORS 등 → 브라우저 로컬 엔진 */
      }
      if (!out || typeof out !== 'object') {
        diag.source = 'js-fallback'
        out = analyzeHarmonyPayload(body)
      } else {
        diag.source = 'backend'
        diag.theoryBaseApplied = out.theoryBase ?? out.theoryBaseApplied ?? null
      }
      // JS 엔진 결과·백엔드 결과 모두 동일하게 NCT(비화성음) 보강 (JS 엔진 자체 로직은 Python과 100% 동일하도록 순수 화음 판정만 함)
      enrichWithNct(out, body)
      console.log('[kordAI-diag]', diag)
      const finalResult = out && typeof out === 'object' ? out : { measures: [] }
      finalResult.theoryBase = theoryBase
      if (out?.theoryRules) finalResult.theoryRules = out.theoryRules
      if (appMode === 'solve') {
        finalResult.grade = gradeHarmony(finalResult, {
          score,
          timeSignature,
          progression: solveProgression,
        })
      }
      applyChordSymbolHints(finalResult, chordSymbolHintsRef.current)
      chordSymbolHintsRef.current = []
      setAnalysisResult(finalResult)
    } catch (e) {
      setAnalysisError(e?.message || String(e))
    } finally {
      setAnalysisLoading(false)
    }
    queueMicrotask(() => {
      analysisSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    })
  }

  /**
   * 로마숫자 표기 수동 오버라이드 (예: 공통화음 전조 — "C: v♭\nF: ii" 처럼
   * 줄바꿈으로 원조/새조 두 줄을 병기). beatIndex가 null이면 마디 단위 chordSymbol.
   */
  const handleOverrideChordSymbol = (measureIndex, beatIndex, value) => {
    setAnalysisResult((prev) => {
      if (!prev?.measures) return prev
      const measures = prev.measures.map((m, idx) => {
        if (idx !== measureIndex) return m
        if (beatIndex == null) return { ...m, chordSymbol: value }
        const beats = (m.beats ?? []).map((b, bi) => (bi === beatIndex ? { ...b, chordSymbol: value } : b))
        return { ...m, beats }
      })
      return { ...prev, measures }
    })
  }

  const handleSolve = () => {
    if (!givenVoice) {
      setSolveWarnings(['먼저 "문제 생성"에서 소프라노·베이스·소프라노+베이스 중 하나를 눌러 문제를 만드세요.'])
      return
    }
    if (givenVoice === 'both') {
      setSolveWarnings(['소프라노+베이스 문제의 내성(알토·테너) 자동 채우기는 아직 준비 중입니다. 지금은 한 성부만 준 문제로 풀기를 사용하세요.'])
      return
    }
    if (!score[givenVoice]?.length) {
      setSolveWarnings([`${givenVoice === 'soprano' ? '소프라노' : '베이스'}에 음을 먼저 입력하세요.`])
      return
    }
    setSolving(true)
    setSolveWarnings([])
    try {
      const { score: solved, warnings } = solveFourPartHarmony({
        key: keySignature,
        timeSignature,
        score,
        givenVoice,
        theoryBase,
      })
      setHistory((h) => [...h, score])
      setScore(solved)
      setSolveWarnings(warnings)
    } catch (e) {
      setSolveWarnings([e?.message || String(e)])
    } finally {
      setSolving(false)
    }
  }

  /** 문제 생성: 레벨·중심 리듬으로 화성진행을 짠 뒤, 성부 선율 + 악보 화성 기호.
   *  @param {'soprano'|'bass'|'both'} mode
   */
  const handleGenerateProblem = (mode) => {
    setHistory((h) => [...h, score])
    setSolveWarnings([])
    try {
      const genOpts = {
        key: keySignature,
        timeSignature,
        measureCount,
        baseRhythm,
        solveLevel,
        theoryBase,
      }
      if (mode === 'both') {
        const { progression, soprano, bass, analysisResult: progAnalysis } =
          generateOuterVoicesWithProgression(genOpts)
        setGivenVoice('both')
        setActiveVoice('alto')
        setSolveProgression(progression)
        setAnalysisResult(progAnalysis)
        setShowAnalysis(false)
        setScore({ ...blankScore(getBeatsPerMeasure(timeSignature)), soprano, bass })
        return
      }
      const { progression, notes, analysisResult: progAnalysis } = generateVoiceWithProgression(mode, genOpts)
      setGivenVoice(mode)
      setActiveVoice(mode === 'soprano' ? 'alto' : 'soprano')
      setSolveProgression(progression)
      setAnalysisResult(progAnalysis)
      setShowAnalysis(false)
      setScore({ ...blankScore(getBeatsPerMeasure(timeSignature)), [mode]: notes })
    } catch (e) {
      setSolveProgression(null)
      setAnalysisResult(null)
      setSolveWarnings([e?.message || String(e)])
      setScore(blankScore(getBeatsPerMeasure(timeSignature)))
      setGivenVoice(null)
    }
  }

  /** 현재 문제(입력·생성된 4성부)를 지우고 풀이 모드를 초기 상태로 되돌린다. */
  const handleClearProblem = () => {
    setHistory((h) => [...h, score])
    setScore(blankScore(beatsPerMeasure))
    setGivenVoice(null)
    setSolveWarnings([])
    setSolveProgression(null)
    setAnalysisResult(null)
  }

  const handleKeyChange = (k) => {
    setKeySignature(k)
    setAnalysisResult(null)
  }

  const handleTimeChange = (t) => {
    setTimeSignature(t)
    setScore(blankScore(getBeatsPerMeasure(t)))
    setHistory([])
    setAnalysisResult(null)
    setSelectedMeasure(null)
    setSelectedNote(null)
  }

  return (
    <div className="app-layout">
      <div className="main-area">
        {/* 한 줄 메뉴바: 파일 · 설정 · (중앙 워드마크) · 창 버튼 */}
        <nav className={`app-menubar${isElectron ? ' app-menubar--electron' : ''}`} aria-label="앱 메뉴">
          <div className="app-menubar-start">
            <img
              className="app-menubar-logo"
              src="/favicon.png"
              alt=""
              width={24}
              height={24}
              decoding="async"
              draggable={false}
              aria-hidden="true"
            />
            <button
              type="button"
              className={`app-menubar-item ${fileMenuOpen ? 'active' : ''}`}
              onClick={() => {
                setFileMenuOpen((v) => !v)
                setSettingsOpen(false)
              }}
            >
              파일
            </button>
            <button
              type="button"
              className={`app-menubar-item ${settingsOpen ? 'active' : ''}`}
              onClick={() => {
                setSettingsOpen((v) => !v)
                setFileMenuOpen(false)
              }}
            >
              설정
            </button>
          </div>
          <div className="app-menubar-brand" aria-hidden={false}>
            <img
              className="app-menubar-wordmark"
              src="/kordAI-logo.png"
              alt="kordAI"
              width={220}
              height={44}
              decoding="async"
              draggable={false}
            />
          </div>
          {isElectron ? (
            <>
              <div className="app-menubar-drag" aria-hidden="true" />
              <div className="app-menubar-winbtns">
                <button
                  type="button"
                  className="app-menubar-winbtn"
                  aria-label="최소화"
                  title="최소화"
                  onClick={() => window.kordAI?.windowMinimize?.()}
                >
                  <svg className="app-menubar-winicon" viewBox="0 0 12 12" aria-hidden="true">
                    <path d="M2.5 6h7" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
                  </svg>
                </button>
                <button
                  type="button"
                  className="app-menubar-winbtn"
                  aria-label="최대화"
                  title="최대화"
                  onClick={() => window.kordAI?.windowMaximize?.()}
                >
                  <svg className="app-menubar-winicon" viewBox="0 0 12 12" aria-hidden="true">
                    <rect x="2.5" y="2.5" width="7" height="7" rx="0.5" fill="none" stroke="currentColor" strokeWidth="1.25" />
                  </svg>
                </button>
                <button
                  type="button"
                  className="app-menubar-winbtn app-menubar-winbtn--close"
                  aria-label="닫기"
                  title="닫기"
                  onClick={() => window.kordAI?.windowClose?.()}
                >
                  <svg className="app-menubar-winicon" viewBox="0 0 12 12" aria-hidden="true">
                    <path d="M3 3l6 6M9 3l-6 6" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
            </>
          ) : (
            <div className="app-menubar-drag" aria-hidden="true" />
          )}
        </nav>
        <input
          ref={xmlFileRef}
          type="file"
          accept=".xml,.musicxml,application/xml,text/xml"
          className="tb-file-input"
          aria-hidden="true"
          tabIndex={-1}
          onChange={handleImportMusicXML}
        />
        <FileMenu
          open={fileMenuOpen}
          onOpenChange={setFileMenuOpen}
          onNew={handleNewBlank}
          onOpen={openMusicXMLPicker}
          onSaveReport={handleExportReport}
          onSaveScore={handleExportScore}
          canSaveReport={canExport}
          canSaveScore={canExport}
        />
        <SettingsMenu
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          midiStatus={midiDeviceStatus === 'unsupported' || midiDeviceStatus === 'error'
            ? midiDeviceStatus
            : midiStatus}
          inputs={midiInputs}
          outputs={midiOutputs}
          inputId={midiInputId}
          outputId={midiOutputId}
          onInputIdChange={setMidiInputId}
          onOutputIdChange={setMidiOutputId}
          onRefresh={refreshMidiDevices}
        />
        <header className="app-header">
          <div className="app-header-center">
            <p className="app-sub">{appMode === 'solve' ? '4성부 화성 풀이' : '4성부 화성 분석'}</p>
            <div className="tb-segmented app-mode-toggle" role="group" aria-label="앱 모드 선택">
              <button
                type="button"
                className={`tb-segmented-btn ${appMode === 'analyze' ? 'active' : ''}`}
                onClick={() => setAppMode('analyze')}
                aria-pressed={appMode === 'analyze'}
                title="입력한 4성부를 분석합니다 (로마숫자·전위·성부진행 점검)."
              >
                분석
              </button>
              <button
                type="button"
                className={`tb-segmented-btn ${appMode === 'solve' ? 'active' : ''}`}
                onClick={() => {
                  setAppMode('solve')
                  if (givenVoice === 'both') setActiveVoice('alto')
                  else if (givenVoice === 'soprano') setActiveVoice('alto')
                  else if (givenVoice === 'bass') setActiveVoice('soprano')
                  setSolveWarnings([])
                }}
                aria-pressed={appMode === 'solve'}
                title="주어진 성부를 보고 나머지 성부를 건반으로 입력해 풀거나, 풀기 버튼으로 자동 채웁니다."
              >
                풀이
              </button>
            </div>
          </div>
        </header>

        <Toolbar
          keySignature={keySignature}
          onKeyChange={handleKeyChange}
          timeSignature={timeSignature}
          onTimeChange={handleTimeChange}
          tempoBpm={tempoBpm}
          onTempoBpmChange={handleTempoBpmChange}
          theoryBase={theoryBase}
          onTheoryBaseChange={setTheoryBase}
          volume={volume}
          onVolumeChange={setVolume}
          onPlayHarmony={handlePlayHarmony}
          onStopHarmony={handleStopHarmony}
          canPlayHarmony={canPlayHarmony}
          playingHarmony={playingHarmony}
          onAnalyze={handleAnalyze}
          appMode={appMode}
          onSolve={handleSolve}
          solving={solving}
          measureCount={measureCount}
          onMeasureCountChange={setMeasureCount}
          baseRhythm={baseRhythm}
          onBaseRhythmChange={setBaseRhythm}
          solveLevel={solveLevel}
          onSolveLevelChange={setSolveLevel}
        />

        <div className="score-workspace">
          <ScoreRenderer
            score={score}
            keySignature={keySignature}
            timeSignature={timeSignature}
            activeVoice={activeVoice}
            analysisResult={analysisResult}
            playbackCursor={playbackCursor}
            onNoteChange={handleNoteChange}
            onNoteDelete={handleNoteDelete}
            onMeasureClear={handleMeasureClear}
            onDragStart={handleDragStart}
            selectedNote={selectedNote}
            onSelectNote={handleSelectNote}
            selectedMeasure={selectedMeasure}
            onSelectMeasure={handleSelectMeasure}
            theoryBase={theoryBase}
          />

          {showAnalysis && (
            <div className="analysis-inline-shell" ref={analysisSectionRef}>
              <AnalysisPanel
                result={analysisResult}
                loading={analysisLoading}
                error={analysisError}
                appMode={appMode}
                onOverrideChordSymbol={handleOverrideChordSymbol}
              />
            </div>
          )}

          {appMode === 'solve' && (
            <div className="solve-bar">
              <span className="tb-label-text">문제 생성</span>
              <div className="voice-tabs" role="group" aria-label="문제 생성 — 랜덤 멜로디를 만들 성부 선택">
                <button
                  type="button"
                  className={`vtab ${givenVoice === 'soprano' ? 'active' : ''}`}
                  onClick={() => handleGenerateProblem('soprano')}
                  title="레벨·중심 리듬에 맞는 화성진행을 만들고, 그 화음음으로 소프라노 선율을 채웁니다. 악보에 화성 기호를 표시합니다."
                >
                  소프라노
                </button>
                <button
                  type="button"
                  className={`vtab ${givenVoice === 'bass' ? 'active' : ''}`}
                  onClick={() => handleGenerateProblem('bass')}
                  title="레벨·중심 리듬에 맞는 화성진행을 만들고, 그 화음의 베이스 선율을 채웁니다. 악보에 화성 기호를 표시합니다."
                >
                  베이스
                </button>
                <button
                  type="button"
                  className={`vtab ${givenVoice === 'both' ? 'active' : ''}`}
                  onClick={() => handleGenerateProblem('both')}
                  title="같은 화성진행으로 소프라노와 베이스를 함께 채웁니다. 악보에 화성 기호를 표시합니다."
                >
                  소프라노+베이스
                </button>
                <button
                  type="button"
                  className="vtab vtab-danger"
                  onClick={handleClearProblem}
                  disabled={!givenVoice}
                  title="현재 문제(입력·생성된 4성부)를 지웁니다."
                >
                  ✕ 지우기
                </button>
              </div>
              {solveProgression && (
                <div className="solve-progression" title="생성된 화성진행 (중심 리듬 단위로 교체)">
                  <span className="solve-progression-label">화성진행</span>
                  <span className="solve-progression-romans">{solveProgression.romanSummary}</span>
                </div>
              )}
              {solveWarnings.length > 0 && (
                <div className="solve-warnings">
                  {solveWarnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="piano-dock">
          <div className="input-area">
            <VoiceTabs
              activeVoice={activeVoice}
              onSelect={setActiveVoice}
              disabledVoices={solveDisabledVoices}
              disabledTitle={
                appMode === 'solve' && givenVoice
                  ? '문제에서 주어진 성부입니다. 다른 성부를 선택해 건반으로 입력하세요.'
                  : appMode === 'solve'
                    ? '먼저 위에서 문제를 생성하세요.'
                    : undefined
              }
            />
            <DurationSelector value={duration} onChange={handleDurationChange} />
            <AccidentalSelector value={pendingAccidental} onChange={handleAccidentalSelect} />
          </div>

          <PianoKeyboard
            activeVoice={activeVoice}
            onNoteClick={handleKeyClick}
            lastPitch={[...score[activeVoice]].reverse().find(n => !n.isRest)?.pitch ?? null}
            theoryBase={theoryBase}
            pressedMidis={pressedMidis}
          />
        </div>
      </div>
    </div>
  )
}
